import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Card, Descriptions, Typography, message, CardProps, Tooltip } from 'antd';
import { DescriptionsItemProps } from 'antd/es/descriptions/Item';
import classnames from 'classnames';
import { get } from 'lodash-es';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/config/context';
import i18n from '@/i18n';
import { isIOS, isAndroid, isWindows, isAppleTV, isElectron, isWeb } from '@/infrastructure/product-info';
import { DATE_TIME_FORMAT, formatLaunchTime, formatTimestamp } from '@/infrastructure/utils/timestamp';
import {
  getTips,
  launchIdText,
  processLaunchIdText,
} from '@/presentation/components/issue-list/components/tips';
import { IssueTrackIcon } from '@/presentation/components/issue-track-icon';
import { ModelDisplay } from '@/presentation/components/model-display';
import { Title } from '@/presentation/components/title';
import { FieldKey, getLocalFieldLabelByValue, Ground } from '@/proto/field';
import styles from './index.module.scss';

interface IProps {
  title?: string;
  data: {
    label: string | React.ReactNode;
    onClick?: () => void;
    info?: string | number | React.ReactNode | null;
    color?: string;
    afterIcon?: React.ReactNode;
    render?: () => ReactNode;
    disabled?: boolean;
  }[];
  labelWidth?: string;
  cardProps?: CardProps;
  titleShowIcon?: boolean;
}

export interface ErrorInfo {
  eventTime: string;
  reportTime: string;
  accountId: string;
  qimei: string;
  deviceId: string;
  osVersion: string;
  model: string;
  ground: string;
  scene: string;
  romName: string
  stage: string;
  sdkVersion: string;
  appVersion: string;
  buildNumber: string;
  appVersionMode?: string;
  clientIdentify: string;
  translatedStatus: string;
  bundleId: string;
  launchId: string;
  processLaunchId: string;
  hotPatchVersion?: string;
  shiplyTag?: string;
  abTestTag?: string;
  launchDuration?: number;
  tagsInFilter?: { id?: string | null, name?: string | null, color?: string | null }[];
}

const { Paragraph } = Typography;

export const renderAppVersion = (info: any) => {
  const appVersion = info?.appVersion ?? '';
  const hotPatchVersion = info?.hotPatchVersion ? `(hotpatch:${info.hotPatchVersion})` : '';

  return appVersion ? `${appVersion}${hotPatchVersion}` : '';
};

export const getDefaultErrorInfo = (info: ErrorInfo, isTrack = true, disabledFields?: string[]) => {
  const eventTime = formatTimestamp(info?.eventTime, DATE_TIME_FORMAT);
  const reportTime = formatTimestamp(info?.reportTime, DATE_TIME_FORMAT);
  const isDisabled = (field: string) => {
    if (!disabledFields) return false;

    return disabledFields.includes(field);
  };


  return [
    { label: i18n.t('发生时间'), info: reportTime },
    { label: i18n.t('上报时间'), info: eventTime },
    {
      label: i18n.t('用户ID'), info: info?.accountId ?? '', afterIcon: isTrack
        ? <IssueTrackIcon
          launchId={isIOS() || isAppleTV() ? info?.launchId : undefined}
          processLaunchId={isAndroid() ? info?.processLaunchId : undefined}
          eventTime={eventTime}
          reportTime={reportTime}
          accountId={info?.accountId || ''} /> : undefined,
    },
    {
      label: i18n.t('设备ID'), info: info?.qimei ?? info?.deviceId ?? '',
      afterIcon: isTrack
        ? <IssueTrackIcon
          launchId={isIOS() || isAppleTV() ? info?.launchId : undefined}
          processLaunchId={isAndroid() ? info?.processLaunchId : undefined}
          eventTime={eventTime}
          reportTime={reportTime}
          qimei={info?.qimei ?? info?.deviceId ?? ''} /> : undefined,
    },
    { label: i18n.t('系统版本'), info: info?.osVersion ?? '' },
    { label: i18n.t('厂商'), info: get(info, 'brand', ''), disabled: !isAndroid() },
    { label: i18n.t('机型'), info: <ModelDisplay model={info?.model} /> },
    {
      label: i18n.t('前/后台'),
      info: getLocalFieldLabelByValue(FieldKey.Ground, info?.ground ?? Ground.Unknown),
      disabled: isDisabled(FieldKey.Ground),
    },
    { label: 'Bundle ID', info: info?.bundleId ?? '' },
    { label: i18n.t('APP版本'), info: renderAppVersion(info) },
    { label: i18n.t('构建号'), info: info?.buildNumber ?? '', disabled: (isElectron() || isWeb()) },
    { label: i18n.t('构建类型'), info: info?.appVersionMode ?? '', disabled: (isElectron() || isWeb()) },
    { label: getTips(i18n.t('启动ID'), launchIdText), info: info?.launchId ?? '' },
    { label: getTips(i18n.t('进程启动ID'), processLaunchIdText), info: info?.processLaunchId ?? '', disabled: !isAndroid() },
    { label: i18n.t('消息ID'), info: info?.clientIdentify ?? '' },
    { label: i18n.t('SDK版本'), info: info?.sdkVersion ?? '' },
    { label: i18n.t('使用时长'), info: formatLaunchTime((info.launchDuration ?? 0) * 1000) || '', disabled: isWindows() },
  ].filter(item => item.info !== undefined && !item.disabled);
};

interface CustomEllipsisItemProps extends DescriptionsItemProps {
  copyText?: string;
  color?: string
  onClick?: () => void;
  afterIcon?: React.ReactNode;
}
export const CustomEllipsisItem: FC<CustomEllipsisItemProps> = (props) => {
  const { t } = useTranslation();
  const { children, copyText, color, onClick, afterIcon } = props;
  const [ellipsis, setEllipsis] = useState(false);
  const [flag, setFlag] = useState(0);
  const onEllipsis = (ellipsis: boolean) => {
    setEllipsis(ellipsis);
  };

  const copy = () => {
    message.success(t('复制成功'));
  };
  const onResize = () => {
    setFlag(flag + 1);
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return useMemo(() => (
    <div className={styles.paragraphContainer}>
      <Paragraph className={classnames({
        [styles.paragraph]: true,
        [styles.paragraphClick]: !!onClick,
      })} onClick={onClick} style={{ color }} copyable={ellipsis ? {
        text: !copyText && typeof (children) === 'string' ? children : copyText,
        icon: [<CopyOutlined style={{ color: 'rgb(123, 136, 156)' }} />, <CopyOutlined style={{ color: 'rgb(123, 136, 156)' }} />],
        tooltips: false,
        onCopy: copy,
      } : false} ellipsis={{
        onEllipsis,
        tooltip: children,
      }}>
        {children}
        <span className={styles.str}>
          {children ? <div className={styles.afterIcon}>{afterIcon}</div> : null}
        </span>
      </Paragraph>
      {ellipsis && children ? <div className={styles.ellipsisAfterIcon}>{afterIcon}</div> : null}
    </div>

  ), [flag, ellipsis, children]);
};

export const ProblemDetailInfo: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { title, cardProps, titleShowIcon } = props;
  const config = useAppConfig();
  const enableColumns = props.data?.filter(item => !item?.disabled) ?? [];

  return <Card bordered={false} {...cardProps}>
    <Descriptions
      title={title ? <Title showIcon={titleShowIcon} title={title} style={{ marginBottom: '4px' }} /> : undefined}
      labelStyle={{
        fontSize: '14px',
        // width: props.labelWidth ?? '104px',
        color: '#74747F',
        whiteSpace: 'nowrap',
      }}
      contentStyle={{
        fontSize: '14px',
        color: '#181829',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline',
      }}
      column={{ xxl: 3, xl: 2, lg: 2, md: 2, sm: 2, xs: 1 }}
    >
      {enableColumns.map((item, index) => {
        // 注：'退出原因' 是 label 比较点，调用方传入的 label 也保留中文常量做匹配
        if (item.label === t('退出原因') && item.info === 'REASON_LOW_MEMORY') {
          return (
            <Descriptions.Item
              label={item.label}
              contentStyle={{
                color: item.color || '#000000',
              }}
              style={{ paddingBottom: '12px' }}
              key={index}
            >
              {item.render ? (
                item.render()
              ) : (
                <Tooltip
                  title={<span>{t('低内存问题优化可参考')}<a target={'_blank'} href={config.memoryOptimizationHref}>{t('Bugly Native内存详情')}</a>{t('监控功能文档')}</span>}
                  overlayStyle={{ maxWidth: 500 }}
                ><div style={{ display: 'inline-block' }}>
                    <CustomEllipsisItem
                      afterIcon={item.afterIcon}
                      onClick={item?.onClick}
                      color={item.color}
                    >
                      {item.info ?? ''}{' '}
                      <QuestionCircleOutlined
                        style={{ color: '#A3A3A9', fontSize: '14px' }}
                      />
                    </CustomEllipsisItem>
                  </div>
                </Tooltip>
              )}
            </Descriptions.Item>
          );
        };
        return <Descriptions.Item
          label={item.label}
          contentStyle={{
            color: item.color || '#000000',
          }}
          style={{ paddingBottom: '12px' }}
          key={index}
        >
          {item.render ? item.render() : <CustomEllipsisItem
            afterIcon={item.afterIcon}
            onClick={item?.onClick}
            color={item.color}>{item.info ?? ''}</CustomEllipsisItem>}
        </Descriptions.Item>;
      })}
    </Descriptions>
  </Card>;
};
